import { useEffect, useCallback } from 'react';

// Thin wrapper around socket call:* events for conference calls
export function useCallSocket({ socket, onRing, onJoined, onUserLeft, onEnded, onRejected, onCancelled, onParticipantStatus, onRemoved, onMissed, onError }) {

	useEffect(() => {
		if (!socket) return;

		const handlers = {
			'call:ring': onRing,
			'call:joined': onJoined,
			'call:user-left': onUserLeft,
			'call:ended': onEnded,
			'call:rejected': onRejected,
			'call:cancelled': onCancelled,
			'call:participant-status': onParticipantStatus,
			'call:removed': onRemoved,
			'call:missed': onMissed,
			'call:error': onError,
		};

		const attached = [];
		Object.entries(handlers).forEach(([event, handler]) => {
			if (typeof handler !== 'function') return;
			socket.on(event, handler);
			attached.push([event, handler]);
		});

		return () => {
			attached.forEach(([event, handler]) => socket.off(event, handler));
		};
	}, [socket, onRing, onJoined, onUserLeft, onEnded, onRejected, onCancelled, onParticipantStatus, onRemoved, onMissed, onError]);

	const startCall = useCallback(({ callType, chatId, invitedUsers, roomId }) =>
		new Promise((resolve) => {
			if (!socket) return resolve({ ok: false, error: 'No socket' });
			socket.emit('call:start', { callType, chatId, invitedUsers, roomId }, resolve);
		}), [socket]);

	const acceptCall = useCallback((roomId, password) =>
		new Promise((resolve) => {
			if (!socket) return resolve({ ok: false });
			socket.emit('call:accept', { roomId, password }, resolve);
		}), [socket]);

	const rejectCall = useCallback((roomId) => {
		if (socket) socket.emit('call:reject', { roomId });
	}, [socket]);

	const cancelCall = useCallback((roomId) => {
		if (socket) socket.emit('call:cancel', { roomId });
	}, [socket]);

	const joinCall = useCallback((roomId, password) =>
		new Promise((resolve) => {
			if (!socket) return resolve({ ok: false });
			socket.emit('call:join', { roomId, password }, resolve);
		}), [socket]);

	const leaveCall = useCallback((roomId) => {
		if (socket) socket.emit('call:leave', { roomId });
	}, [socket]);

	const endCall = useCallback((roomId) => {
		if (socket) socket.emit('call:end', { roomId });
	}, [socket]);

	const muteAudio = useCallback((roomId) => {
		if (socket) socket.emit('call:mute-audio', { roomId });
	}, [socket]);

	const unmuteAudio = useCallback((roomId) => {
		if (socket) socket.emit('call:unmute-audio', { roomId });
	}, [socket]);

	const enableVideo = useCallback((roomId) => {
		if (socket) socket.emit('call:enable-video', { roomId });
	}, [socket]);

	const disableVideo = useCallback((roomId) => {
		if (socket) socket.emit('call:disable-video', { roomId });
	}, [socket]);

	const startScreenShare = useCallback((roomId) => {
		if (socket) socket.emit('call:start-screen-share', { roomId });
	}, [socket]);

	const stopScreenShare = useCallback((roomId) => {
		if (socket) socket.emit('call:stop-screen-share', { roomId });
	}, [socket]);

	const removeParticipant = useCallback((roomId, targetUserId) => {
		if (socket) socket.emit('call:remove-participant', { roomId, targetUserId });
	}, [socket]);

	const inviteUser = useCallback((roomId, targetUserId) =>
		new Promise((resolve) => {
			if (!socket) return resolve({ ok: false });
			socket.emit('call:invite', { roomId, targetUserId }, resolve);
		}), [socket]);

	return {
		startCall,
		acceptCall,
		rejectCall,
		cancelCall,
		joinCall,
		leaveCall,
		endCall,
		muteAudio,
		unmuteAudio,
		enableVideo,
		disableVideo,
		startScreenShare,
		stopScreenShare,
		removeParticipant,
		inviteUser,
	};
}
